import * as React from "react";
import {type PropsWithChildren, useEffect, useRef, useState} from "react";
import {Modal, Text} from "@mantine/core";
import {PopUpContext} from "./PopUpContext.tsx";
import {toast} from "react-toastify";
import {useLocation} from "react-router";

interface IPopUp {
	title: string;
	content: React.ReactElement;
}

export function PopUpProvider({children}: PropsWithChildren) {
	const location = useLocation();
	const prevPathname = useRef(location.pathname);
	const [popUps, setPopUps] = useState<IPopUp[]>([]);

	const addPopUp = ({title, content}: {title: string; content: React.ReactElement}) => {
		setPopUps((prev) => [...prev, {title, content}]);
	};

	const clearPopUps = () => {
		setPopUps([]);
	};

	const closePopUp = (index?: number) => {
		if (index == null) {
			setPopUps((prev) => prev.slice(0, -1));
			return;
		}

		setPopUps((prev) => prev.filter((_, i) => i !== index));
	};

	const addAlert = ({
		content,
		severity,
		time,
	}: {
		content: string;
		severity?: "error" | "success" | "info" | "warning";
		time?: number;
	}) => {
		toast(content, {
			type: severity,
			autoClose: time ?? 5000,
		});
	};

	useEffect(() => {
		if (prevPathname.current !== location.pathname) {
			prevPathname.current = location.pathname;
			setPopUps([]);
		}
	}, [location.pathname]);

	return (
		<PopUpContext value={{addPopUp, clearPopUps, closePopUp, addAlert}}>
			{children}
			{popUps.map((x, index) => (
				<Modal
					key={index}
					opened={true}
					onClose={() => closePopUp(index)}
					closeOnClickOutside={false}
					closeOnEscape={true}
					size="90%"
					centered
					radius="md"
					shadow="xl"
					zIndex={250 + index * 10}
					overlayProps={{
						backgroundOpacity: 0.55,
						blur: 3,
						onDoubleClick: () => closePopUp(index),
					}}
					title={
						<Text fw={700} size="lg">
							{x.title}
						</Text>
					}
					styles={{
						header: {
							borderBottom: "1px solid var(--mantine-color-default-border)",
							paddingBottom: "12px",
						},
						content: {
							maxHeight: "94vh",
							display: "flex",
							flexDirection: "column",
						},
						body: {
							flex: 1,
							overflowY: "auto",
							padding: "16px",
						},
					}}
				>
					{x.content}
				</Modal>
			))}
		</PopUpContext>
	);
}
